import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { UseAdRevertModalArgs, UseAdRevertModalResult } from '../types';

// Revert is in-memory and instant. Hold the modal visible briefly so the
// user perceives the transition before the UI updates.
const REVERT_VISUAL_DELAY_MS = 400;

export const useAdRevertModal = ({ onClose }: UseAdRevertModalArgs): UseAdRevertModalResult => {
  const { formatMessage } = useIntl();
  const revert = useAnalyticalDatasetDefinitionStore((s) => s.revert);

  // Fire-and-close exactly once on mount. Closing over `revert`/`onClose`
  // captures the values that existed when the modal opened — which is the
  // semantic we want; no need to re-run when those refs change. Cleanup
  // clears the timer on unmount (e.g. the user navigates away mid-delay).
  useEffect(
    () => {
      revert();
      const timer = setTimeout(onClose, REVERT_VISUAL_DELAY_MS);
      return () => clearTimeout(timer);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    title: formatMessage({ id: 'analyticalDatasetDetail.revertModal.title' }),
    loadingTitle: formatMessage({ id: 'analyticalDatasetDetail.revertModal.loadingTitle' }),
    loadingText: formatMessage({ id: 'analyticalDatasetDetail.revertModal.loadingText' }),
  };
};
